export type IncidentStatus =
  | 'Archived'
  | 'Open'
  | 'New'
  | 'Under Review'
  | 'Reviewed - Unaccounted'
  | 'Reviewed - Accounted'
  | 'Pending Response'
  | 'Practice Issue'
  | 'Investigation Resolved'
  | 'Unresolved';

/**
 * Dropdown row: `value` is FK `midas.IncidentStatus.id`. `uiStatus` drives icons/colors.
 */
export type IncidentStatusOption = {
  readonly label: string;
  readonly value: number;
  readonly uiStatus: IncidentStatus;
};

/**
 * Incident Status Icons Configuration
 * Maps incident status values to their corresponding image paths
 */
export const INCIDENT_STATUS_ICONS: Record<IncidentStatus, string> = {
  Archived: '/archived.svg',
  'Investigation Resolved': 'pi pi-file-check',
  New: '/variance-status/New%20filled.svg',
  Open: '/variance-status/variance-open.svg',
  'Pending Response': '/variance-status/incident-pending-response.svg',
  'Practice Issue': '/variance-status/incident-practice-issue.svg',
  'Reviewed - Accounted': '/variance-status/incident-reviewed-accounted.svg',
  'Reviewed - Unaccounted': '/variance-status/incident-reviewed-unaccounted.svg',
  'Under Review': '/variance-status/incident-under-review.svg',
  Unresolved: '/variance-status/incident-unresolved.svg',
} as const;

export const INCIDENT_STATUS_ICON_COLORS: Record<IncidentStatus, string> = {
  Archived: 'text-red-400',
  'Investigation Resolved': 'text-lime-400',
  New: 'text-white',
  Open: 'text-white',
  'Pending Response': 'text-amber-400',
  'Practice Issue': 'text-lime-400',
  'Reviewed - Accounted': 'text-lime-400',
  'Reviewed - Unaccounted': 'text-red-400',
  'Under Review': 'text-amber-400',
  Unresolved: 'text-red-500',
} as const;

export function getIncidentStatusIconColor(status: IncidentStatus): string {
  const key = (typeof status === 'string' ? status.trim() : '') as IncidentStatus;
  return INCIDENT_STATUS_ICON_COLORS[key] ?? 'text-white';
}

export const INCIDENT_STATUS_OPTIONS: readonly IncidentStatusOption[] = [
  { label: 'Archived', value: 1, uiStatus: 'Archived' },
  { label: 'Investigation Resolved', value: 2, uiStatus: 'Investigation Resolved' },
  { label: 'New', value: 3, uiStatus: 'New' },
  { label: 'Open', value: 4, uiStatus: 'Open' },
  { label: 'Pending Response', value: 5, uiStatus: 'Pending Response' },
  { label: 'Practice Issue', value: 6, uiStatus: 'Practice Issue' },
  { label: 'Reviewed - Accounted', value: 7, uiStatus: 'Reviewed - Accounted' },
  { label: 'Reviewed - Unaccounted', value: 8, uiStatus: 'Reviewed - Unaccounted' },
  { label: 'Under Review', value: 9, uiStatus: 'Under Review' },
  { label: 'Unresolved', value: 10, uiStatus: 'Unresolved' },
] as const;

export const INCIDENT_STATUS_SELECT_OPTIONS: readonly { label: string; value: number }[] =
  INCIDENT_STATUS_OPTIONS.map((o) => ({ label: o.label, value: o.value }));

export function getUiIncidentStatusForStatusId(id: number | null | undefined): IncidentStatus {
  if (id == null || !Number.isFinite(id)) {
    return 'Open';
  }
  const row = INCIDENT_STATUS_OPTIONS.find((o) => o.value === id);
  return row?.uiStatus ?? 'Open';
}

export function getIncidentStatusIdForUi(ui: IncidentStatus): number | undefined {
  return INCIDENT_STATUS_OPTIONS.find((o) => o.uiStatus === ui)?.value;
}

export function getIncidentStatusIcon(status: IncidentStatus): string {
  const key = (typeof status === 'string' ? status.trim() : '') as IncidentStatus;
  if (key.toLowerCase() === 'unresolved') {
    return '/variance-status/incident-unresolved.svg';
  }
  return INCIDENT_STATUS_ICONS[key] ?? '/variance-status/variance-open.svg';
}

/** Public URL path (leading slash) for email / fetch; maps PrimeNG-only statuses to an SVG asset. */
export function getIncidentStatusEmailAssetPath(status: string): string {
  const key = (typeof status === 'string' ? status.trim() : '') as IncidentStatus;
  if (key.toLowerCase() === 'unresolved') {
    return '/variance-status/incident-unresolved.svg';
  }
  const icon = INCIDENT_STATUS_ICONS[key];
  if (!icon) {
    return '/variance-status/variance-open.svg';
  }
  if (icon.startsWith('pi ')) {
    return '/variance-status/incident-investigator-resolved.svg';
  }
  return icon.startsWith('/') ? icon : `/${icon}`;
}
